import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Info } from 'lucide-react';

const SentimentOverview = ({ data }) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const total = Object.values(data).reduce((acc, val) => acc + val, 0);
  
  // Format data for chart
  const chartData = [
    { name: 'Positive', value: data.Promoter || 0, percentage: Math.round(((data.Promoter || 0) / total) * 100) },
    { name: 'Neutral', value: data.Passive || 0, percentage: Math.round(((data.Passive || 0) / total) * 100) },
    { name: 'Negative', value: data.Detractor || 0, percentage: Math.round(((data.Detractor || 0) / total) * 100) }
  ];
  
  // Custom colors for sentiment types
  const COLORS = {
    Positive: '#22c55e',
    Neutral: '#94a3b8',
    Negative: '#ef4444'
  };
  
  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 shadow-md rounded-md">
          <p className="font-medium">{payload[0].payload.name}</p>
          <p className="text-sm">
            <span className="font-semibold">{payload[0].value}</span> responses
            <span className="ml-1 text-gray-500">({payload[0].payload.percentage}%)</span>
          </p>
        </div>
      );
    }
    return null;
  };
  
  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 h-full"
    >
      <div className="flex justify-between items-start mb-6">
        <div>
          <div className="flex items-center space-x-2">
            <h3 className="text-lg font-semibold text-gray-800">Sentiment Overview</h3>
            <div className="relative">
              <div
                onMouseEnter={() => setIsPopoverOpen(true)}
                onMouseLeave={() => setIsPopoverOpen(false)}
                className="flex items-center justify-center w-5 h-5 rounded-full hover:bg-gray-100 transition-colors cursor-help"
              >
                <Info className="w-4 h-4 text-gray-400 hover:text-blue-600" />
              </div>
              
              {isPopoverOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                  className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 z-30"
                  onMouseEnter={() => setIsPopoverOpen(true)}
                  onMouseLeave={() => setIsPopoverOpen(false)}
                >
                  <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-4 w-80">
                    <div className="text-sm text-gray-700">
                      <p className="font-semibold text-gray-800 mb-2">How sentiments are detected:</p>
                      <p className="leading-relaxed">
                        The model detects positive, negative, and neutral parts of the text. It weighs their intensity and importance to decide the overall sentiment based on the dominant tone.
                      </p>
                    </div>
                    {/* Arrow pointing up */}
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-white"></div>
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-200" style={{ marginBottom: '-1px' }}></div>
                  </div>
                </motion.div>
              )}
            </div>
          </div>
          <p className="text-sm text-gray-500">Overview of customer feedback sentiments</p>
        </div>
      </div>
      
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={true} horizontal={true} stroke="#d1d5db" strokeWidth={1} />
            <XAxis 
              dataKey="name" 
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#6b7280' }}
            />
            <YAxis 
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#6b7280' }}
              width={30}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar 
              dataKey="value" 
              radius={[4, 4, 0, 0]}
              barSize={60}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[entry.name]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      
      <div className="grid grid-cols-3 gap-4 mt-8">
        <div className="flex flex-col items-center">
          <div className="w-3 h-3 rounded-full bg-green-500 mb-1"></div>
          <div className="text-sm font-medium">Positive</div>
          <div className="text-gray-500 text-xs">{data.Promoter || 0} responses</div>
        </div>
        <div className="flex flex-col items-center">
          <div className="w-3 h-3 rounded-full bg-gray-400 mb-1"></div>
          <div className="text-sm font-medium">Neutral</div>
          <div className="text-gray-500 text-xs">{data.Passive || 0} responses</div>
        </div>
        <div className="flex flex-col items-center">
          <div className="w-3 h-3 rounded-full bg-red-500 mb-1"></div>
          <div className="text-sm font-medium">Negative</div>
          <div className="text-gray-500 text-xs">{data.Detractor || 0} responses</div>
        </div>
      </div>
    </motion.div>
  );
};

export default SentimentOverview;
